import { useState, useCallback } from "react";
import type { FileObjectType } from "@jsq/ds/core";
import {
  useGeneratePresignedUrlsMutation,
  useCreateJobMutation,
} from "~/graphql/supergraphHooks";
import {
  DataStudioJobTypeEnum,
  type DataStudioCreateJobInput,
} from "~/graphql/supergraphTypes";
import { uploadFilesToS3 } from "~/utils/s3UploadClient";

export interface DataStudioUploadFilesParams {
  files: FileObjectType[];
  environment: string;
  jobType: DataStudioJobTypeEnum;
  arenaDomain: string;
  arenaUserEmail: string;
}

export interface DataStudioCreateJobParams {
  jobType: DataStudioJobTypeEnum;
  arenaDomain: string;
  arenaName?: string;
  arenaUserEmail: string;
  env: string;
  jobConfig: {
    tools: Array<{
      toolId: string;
      toolType: string;
      options: Array<{
        key: string;
        value: string;
      }>;
    }>;
  };
  jobId?: string;
}

export interface DataStudioCreateJobResult {
  success: boolean;
  jobId?: string;
  s3Uri?: string;
  message?: string;
}

/**
 * A React hook for Data Studio file uploads using presigned URLs.
 * Follows the same pattern as useComplianceUploadFilesToS3.
 *
 * @returns An object containing:
 * - uploadFilesToS3: Function to upload files to Data Studio S3 bucket
 * - createJob: Function to create a Data Studio job
 * - loading: Boolean indicating if any operation is in progress
 */
export default function useDataStudioUploadFiles() {
  const [loading, setLoading] = useState(false);

  const [generatePresignedUrlsMutation] = useGeneratePresignedUrlsMutation();

  const [createJobMutation] = useCreateJobMutation();

  const uploadFiles = useCallback(
    (params: DataStudioUploadFilesParams, retryCount = 3) => {
      setLoading(true);

      const input = {
        environment: params.environment,
        jobType: params.jobType,
        arenaDomain: params.arenaDomain,
        arenaUserEmail: params.arenaUserEmail,
        files: params.files.map((fileObj) => ({
          filename: fileObj.file.name,
          contentType: fileObj.file.type || "application/octet-stream",
          contentLength: fileObj.file.size,
        })),
      };

      return generatePresignedUrlsMutation({
        variables: { input },
      })
        .then((urlResponse) => {
          const result = urlResponse.data?.dataStudioGeneratePresignedUrls;

          if (!result?.success || !result.presignedUrlObjs) {
            const errorMessage =
              result?.errors?.join(", ") || "Failed to generate presigned URLs";
            throw new Error(errorMessage);
          }

          const presignedObjs = result.presignedUrlObjs.map((obj) => ({
            s3Url: obj.s3Url,
            objectKey: obj.objectKey,
            fileName: obj.filename,
          }));

          const filesToUpload = params.files.map((fileObj, index) => ({
            generatedUrlObj: presignedObjs[index],
            file: fileObj.file,
          }));

          return uploadFilesToS3(filesToUpload, retryCount);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [generatePresignedUrlsMutation],
  );

  const createJob = useCallback(
    async (params: DataStudioCreateJobParams): Promise<DataStudioCreateJobResult> => {
      setLoading(true);

      try {
        const input: DataStudioCreateJobInput = {
          jobType: params.jobType,
          arenaDomain: params.arenaDomain,
          arenaName: params.arenaName,
          arenaUserEmail: params.arenaUserEmail,
          env: params.env,
          jobConfig: params.jobConfig,
          jobId: params.jobId,
        };

        const response = await createJobMutation({
          variables: { input },
        });

        const result = response.data?.dataStudioCreateJob;

        if (!result?.id) {
          throw new Error("No response data received from create job mutation");
        }

        return {
          success: true,
          jobId: result.id,
          s3Uri: result.s3Uri ?? undefined,
          message: result.message ?? undefined,
        };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
        throw new Error(`Data Studio job creation failed: ${errorMessage}`);
      } finally {
        setLoading(false);
      }
    },
    [createJobMutation],
  );

  return {
    uploadFilesToS3: uploadFiles,
    createJob,
    loading,
  };
}
